import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, FieldGroup, FieldRow, FieldSet } from "react-corner-ds";
import { Trans, useTranslation } from "react-i18next";
import { WebAccount } from "../../../models/response/response.model";
import style from "./CtWidgetDemoSuccess.module.css";
import copyLogo from "../../../assets/img/demo/copy.svg";
import infoLogo from "../../../assets/img/demo/info.svg";
import qrCodeLogo from "../../../assets/img/demo/qrCode.svg";
import qrCodeFrLogo from "../../../assets/img/demo/qrCodeFr.svg";

interface ICtWidgetDemoSuccess {
  webAccount?: WebAccount;
  contactMail?: string;
  stepForm?: string;
}

const CtWidgetDemoSuccess: React.FC<ICtWidgetDemoSuccess> = ({
  webAccount,
  contactMail,
  stepForm,
}) => {
  const { t, i18n } = useTranslation();
  const [isCredential, setIsCredential] = useState<boolean>(false);
  const [isShowTooltip, setIsShowTooltip] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1023);

  const hostName = window.location.host;
  const loginPlatform =
    hostName.includes("cornertrader.ch") || hostName.includes("swisspost")
      ? `https://demo.platform.cornertrader.ch`
      : `https://demo.platform.cornertrader.com`;

  useEffect(() => {
    setIsCredential(
      webAccount &&
        webAccount?.ExtUserID !== "" &&
        webAccount?.PasswordClear !== ""
        ? true
        : false,
    );
  }, [webAccount]);

  const scrollDataWidget = document.querySelector(
    '[data-widget="ct-widget-demo"]',
  )?.scrollTop;

  window.scrollTo({ top: scrollDataWidget, behavior: "smooth" });

  useEffect(() => {
    window.buildSuccessFormPageName("demo");
  }, []);

  const showTitle = useMemo(() => {
    switch (stepForm) {
      case "CountryNotAllowed":
        return <h2>{t`success.demo.pageTitleCountryNotAllowed`}</h2>;

      case "CountryAlmostAllowed":
        return <h2>{t`success.demo.pageTitleCountryAlmostAllowed`}</h2>;

      default:
        return isCredential ? (
          <h2>{t`success.demo.pageTitleCredential`}</h2>
        ) : (
          <h2>{t`success.demo.pageTitleFormSubmitted`}</h2>
        );
    }
  }, [isCredential, stepForm, t]);

  const showSubtitle = useMemo(() => {
    switch (stepForm) {
      case "CountryNotAllowed":
        return <h4>{t`success.demo.pageSubtitleCountryNotAllowed`}</h4>;

      case "CountryAlmostAllowed":
        return (
          <h4>
            <Trans i18nKey={t`success.demo.pageSubtitleCountryAlmostAllowed`} />
          </h4>
        );

      default:
        return isCredential ? (
          <span>
            <Trans
              i18nKey={t`success.demo.pageSubtitleCredential`}
              components={{
                Value: (
                  <span className={style.demoSuccessValue}>{contactMail}</span>
                ),
              }}
              values={{ contactMail: contactMail }}
            />
          </span>
        ) : (
          <h4>{t`success.demo.pageSubtitleFormSubmitted`}</h4>
        );
    }
  }, [contactMail, isCredential, stepForm, t]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 1023);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleSaveContact = useCallback(() => {
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      "N:;Cornèrtrader;;;",
      "FN:Relationship Manager",
      `TEL;TYPE=WORK,VOICE:${
        i18n.language?.startsWith("fr")
          ? "+41 22 749 80 80"
          : "+41 58 880 80 80"
      }`,
      "EMAIL;TYPE=WORK:info@cornertrader.ch",
      "ORG:Cornèrtrader",
      "URL:www.cornertrader.ch",
      "ADR;TYPE=WORK:;;Tödistrasse 27, CH-8002, Zurich - Switzerland;;;;",
      "END:VCARD",
    ].join("\r\n");

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contact.vcf";
    a.click();
    URL.revokeObjectURL(url);
  }, [i18n.language]);

  const showDisclaimer = useMemo(() => {
    return (
      <>
        <FieldGroup customCssClass={style.disclaimer}>
          <Trans
            i18nKey={t`success.demo.disclaimer`}
            components={{
              Disclaimer: <a href="mailto:info@cornertrader.ch" />,
            }}
          />
        </FieldGroup>

        <FieldRow customCssClass={style.disclaimerRow}>
          {isMobile ? (
            <FieldGroup customCssClass={style.mobileDisclaimer}>
              <h2>{t`success.demo.numberSection.description`}</h2>
              <h2>{t`success.demo.numberSection.number`}</h2>
              <button
                type="button"
                onClick={handleSaveContact}
                data-trk-btn-click="contact-creation-byCTA"
              >
                <span>{t`success.demo.numberSection.button`}</span>
              </button>
            </FieldGroup>
          ) : (
            <FieldGroup customCssClass={style.desktopDisclaimer}>
              <div>
                <h2>{t`success.demo.numberSection.description`}</h2>
                <h2>{t`success.demo.numberSection.number`}</h2>
                <h2>{t`success.demo.numberSection.helpQrCode`}</h2>
              </div>
              <img
                src={
                  i18n.language?.startsWith("fr") ? qrCodeFrLogo : qrCodeLogo
                }
                alt="QR Code"
                className={`${style.disclaimerQrImg} trkDemo`}
                width={200}
                height={200}
                data-trk-btn-click="contact-creation-byQR"
              />
            </FieldGroup>
          )}
        </FieldRow>
      </>
    );
  }, [handleSaveContact, i18n.language, isMobile, t]);

  const copyToClipboard = (text?: string) => {
    const textToCopy = text ?? "";
    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsShowTooltip(true);
      setTimeout(() => setIsShowTooltip(false), 750);
    });
  };

  const demoLoginLink = useCallback(
    () => window.open(loginPlatform, "_blank"),
    [loginPlatform],
  );

  useEffect(() => {
    const scrollEl = document.querySelector(".ct-modal .container");
    scrollEl?.scrollTo({ top: 0, left: 0 });
  }, []);

  const widgetWithCredential = useMemo(() => {
    return (
      <div className={style.demoSuccess}>
        <FieldSet>
          <FieldRow customCssClass={style.demoSuccessCustomRow}>
            <FieldGroup customCssClass={style.demoSuccessTitle}>
              {showTitle}
            </FieldGroup>
            <FieldGroup>
              <h4>{t`success.demo.credential`}</h4>
            </FieldGroup>
            <FieldGroup customCssClass={style.demoSuccessSubTitle}>
              <img src={infoLogo} alt="info logo" width={22} height={22} />
              {showSubtitle}
            </FieldGroup>
          </FieldRow>

          <FieldRow customCssClass={style.demoSuccessCustomRow}>
            <FieldGroup customCssClass={style.demoSuccessCredentials}>
              <h4 className={style.demoSuccessLabel}>
                {t`success.demo.userId`}
              </h4>
              <h4
                className={style.demoSuccessClipboardBox}
                onClick={() => copyToClipboard(webAccount?.ExtUserID)}
              >
                <span className={style.demoSuccessValue}>
                  {webAccount?.ExtUserID}
                </span>
                <img src={copyLogo} alt="copy icon" />
              </h4>
            </FieldGroup>
            <FieldGroup customCssClass={style.demoSuccessCredentials}>
              <h4 className={style.demoSuccessLabel}>
                {t`success.demo.password`}
              </h4>
              <h4
                className={style.demoSuccessClipboardBox}
                onClick={() => copyToClipboard(webAccount?.PasswordClear)}
              >
                <span className={style.demoSuccessValue}>
                  {webAccount?.PasswordClear}
                </span>
                <img src={copyLogo} alt="copy icon" />
              </h4>
            </FieldGroup>
          </FieldRow>

          {isShowTooltip && (
            <div className={style.demoSuccessTooltip}>
              <span>
                <i className={`${style.customIcon} cticonapp-ok`}></i>
                {t`success.demo.tooltip`}
              </span>
            </div>
          )}

          <FieldRow>
            <FieldGroup customCssClass={style.customButton}>
              <button
                type="button"
                onClick={() => demoLoginLink()}
                className={`${style.demoSuccessButton} trkDemo`}
              >
                <span>{t`success.demo.demoLoginButton`}</span>
              </button>
            </FieldGroup>
          </FieldRow>

          <FieldRow>
            <FieldGroup>{showDisclaimer}</FieldGroup>
          </FieldRow>
        </FieldSet>
      </div>
    );
  }, [
    showTitle,
    t,
    showSubtitle,
    webAccount?.ExtUserID,
    webAccount?.PasswordClear,
    isShowTooltip,
    showDisclaimer,
    demoLoginLink,
  ]);

  const widgetWithoutCredential = useMemo(() => {
    return (
      <div className={style.demoSuccess}>
        <FieldSet>
          <FieldRow>
            <FieldGroup customCssClass={style.demoSuccessTitle}>
              {stepForm === "FormSubmitted" ? (
                <h2>
                  <i className="cticonapp-ok-2"></i>
                </h2>
              ) : (
                <></>
              )}
              {showTitle}
            </FieldGroup>
          </FieldRow>
          <FieldRow>
            <FieldGroup customCssClass={style.demoSuccessSubTitle}>
              {showSubtitle}
            </FieldGroup>
          </FieldRow>
          <FieldRow>
            <FieldGroup customCssClass={style.customButton}>
              <Button
                type="button"
                customCssClass={`${style.closeButton} close-modal`}
                text={t`success.demo.closeButton`}
              />
            </FieldGroup>
          </FieldRow>
          <FieldRow>
            <FieldGroup>{showDisclaimer}</FieldGroup>
          </FieldRow>
        </FieldSet>
      </div>
    );
  }, [showDisclaimer, showSubtitle, showTitle, stepForm, t]);

  return isCredential ? widgetWithCredential : widgetWithoutCredential;
};

export default CtWidgetDemoSuccess;
